import axios from 'axios';
import { API_URLS } from '../urls';
import { BackendResponse, handleError, extractServerConflictMessage } from '../../dataTypes/backendServicesManagment/types';
import { Sound, initializedSound } from '../../dataTypes/mediaManagment/sound_types';

import { getUserAgentInformations   }  from '../../utils/utils_userAgent';
import { getAppInformations         }  from '../../utils/utils_app';

import { createUpload               }  from '../fileManagment/uploadWithStore';

/**
 * Lance l'upload du son en arrière-plan, quelle que soit sa taille (uniformisation
 * du mécanisme d'upload : toujours par chunks, comme pour la vidéo/le document).
 */
const uploadSoundInBackground = (soundId: number, soundFileToUpload: File): void => {
  const uploadConfig = {
    uploadChunkUrl: API_URLS.uploadSoundChunk,
    finalizeUploadUrl: API_URLS.finalizeSoundUpload,
    chunkSize: 5 * 1024 * 1024, // 5MB pour les sons
    maxRetries: 3,
    enableResume: true,
    entityId: soundId,
    entityType: 'sound',
    autoStart: true,
    metadata: {
      soundId: soundId,
      mediaType: 'sound'
    }
  };

  const uploader = createUpload(soundFileToUpload, uploadConfig);
  console.log(`Upload son démarré en arrière-plan pour le son ID: ${soundId}, Upload ID: ${uploader.id}`);
};

export interface BackendResponse_SoundList extends BackendResponse<Sound[]> {}

// Fonction pour récupérer les sons
export const getSoundList = async (adminId?: number): Promise<BackendResponse_SoundList> => {

  const url = API_URLS.getSoundList;

  try
   {
    const response = await axios.get(url, { params: adminId ? { adminId } : {} });

    if (response.status === 200)
          {
           const sounds: Sound[] = response.data.map((sound: any) => {
             const defaultSound = initializedSound();

             const categoriesIds = sound.mediaCategories?.map((category: any) => category.id) || [];
             const keywordsIds   = sound.keywords?.map((keyword: any) => keyword.id) || [];

             return {
               ...defaultSound,
               id                  : sound.id           ?? defaultSound.id,
               uuid                : sound.uuid         ?? defaultSound.uuid,
               publicRef           : sound.publicRef         ?? defaultSound.publicRef,
               mediaType           : sound.mediaType    ?? defaultSound.mediaType,
               name                : sound.name         ?? defaultSound.name,
               thumbnail           : sound.thumbnail    ?? defaultSound.thumbnail,
               comment             : sound.comment      ?? defaultSound.comment,
               state               : sound.state        ?? defaultSound.state,
               creationDate        : sound.creationDate ? new Date (sound.creationDate.date) : defaultSound.creationDate,
               creationIpAddress   : sound.creationIpAddress ?? defaultSound.creationIpAddress,
               keywordsIds         : keywordsIds        ?? defaultSound.keywordsIds,
               categoriesIds       : categoriesIds      ?? defaultSound.categoriesIds,
               associatedProductId : sound.associatedProductId ?? defaultSound.associatedProductId,
               publicPortal        : sound.publicPortal,

               active              : sound.active       ?? defaultSound.active,
               visible             : sound.visible      ?? defaultSound.visible,
               title               : sound.title        ?? defaultSound.title,
               description         : sound.description  ?? defaultSound.description,
               soundFile           : sound.soundFile    ?? defaultSound.soundFile,
               originalFile        : sound.originalFile      ?? defaultSound.originalFile,

               storageBytes        : sound.storageBytes      ?? defaultSound.storageBytes,
               storageComputedAt   : sound.storageComputedAt  ? new Date (sound.storageComputedAt.date) : defaultSound.storageComputedAt,
             };
           });

           return {
                    code    : 1,
                    comment : 'The retrieval of sounds is complete.',
                    payload : sounds,
                  };
          }
     else {
           return handleError<Sound[]>(response.status, 'Non-200 response from API');
          }
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
     {
      return handleError<Sound[]>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<Sound[]>(-102, 'Unknown error');
  }
};

export interface BackendResponse_Sound extends BackendResponse<Sound> {}

export const getSoundById = async (soundId : number): Promise<BackendResponse_Sound> => {

  const baseUrl = `${API_URLS.getSoundById}`;
  const url = baseUrl.replace('{id}', `${soundId}`);

  try 
   {
    const response = await axios.get(url);

    if (response.status === 200) 
          {
           const sound = response.data

             const defaultSound = initializedSound();
             const categoriesIds = sound.mediaCategories?.map((category: any) => category.id) || [];
             const keywordsIds   = sound.keywords?.map((keyword: any) => keyword.id) || [];

             const soundToReturn : Sound = {
               ...defaultSound,
               id            : sound.id           ?? defaultSound.id,
               uuid          : sound.uuid         ?? defaultSound.uuid,
               publicRef     : sound.publicRef         ?? defaultSound.publicRef,
               mediaType     : sound.mediaType    ?? defaultSound.mediaType,
               name          : sound.name         ?? defaultSound.name,
               thumbnail     : sound.thumbnail    ?? defaultSound.thumbnail,
               comment       : sound.comment      ?? defaultSound.comment,
               state         : sound.state        ?? defaultSound.state,
               creationDate  : sound.creationDate ?? defaultSound.creationDate,
               creationIpAddress: sound.creationIpAddress ?? defaultSound.creationIpAddress,
               keywordsIds   : keywordsIds        ?? defaultSound.keywordsIds,
               categoriesIds : categoriesIds      ?? defaultSound.categoriesIds,
               publicPortal  : sound.publicPortal ?? defaultSound.publicPortal,
               active        : sound.active       ?? defaultSound.active,
               visible       : sound.visible      ?? defaultSound.visible,
               title         : sound.title        ?? defaultSound.title,
               description   : sound.description  ?? defaultSound.description,
               soundFile     : sound.soundFile    ?? defaultSound.soundFile,
               originalFile          : sound.originalFile        ?? defaultSound.originalFile,
             };

           return {
                    code    : 1,
                    comment : 'The retrieval of sound is complete.',
                    payload : soundToReturn,
                  };
          } 
     else {
           return handleError<Sound>(response.status, 'Non-200 response from API');
          }
   } 
  catch (error) 
   {
    if (axios.isAxiosError(error)) 
     {
      return handleError<Sound>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<Sound>(-102, 'Unknown error');
  }
};

// Fonction pour mettre à jour la visibilité d'un son
export const updateSoundVisibility = async (id: number, state: number): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.patchSoundVisibility}`;
  const url = baseUrl.replace('{id}', `${id}`);
  const visibilityStr = state === 1 ? 'true' : 'false';

  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'application/merge-patch+json',
  };

  try {
    const response = await axios.patch(url, { visibilityStr }, { headers });

    if (response.status === 200 || response.status === 204) {
      return {
        code: 1,
        comment: 'Sound visibility updated successfully.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Non-200/204 response from API');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }

    return handleError<void>(-102, 'Network error');
  }
};

// Fonction pour mettre à jour un son existant
export const updateSound = async ( id                : number      ,
                                   active            : boolean     ,
                                   title             : string      ,
                                   description       : string      ,
                                   soundFileToUpload : File | null ,
                                   categoriesIds     : number[]    ,
                                   keywordsIds       : number[]    ,
                                   publicPortal      : boolean     ,
                                   adminId?          : number      ): Promise<BackendResponse> => {

  const categoriesIdsStr: string = categoriesIds.join(',');
  const keywordsIdsStr: string = keywordsIds.join(',');

  const url = `${API_URLS.patchSound}`;

  const formData = new FormData();

                     formData.append ( 'id'                , `${id}`                 );
                     formData.append ( 'active'            , active.toString()       );
                     formData.append ( 'title'             , title                   );
                     formData.append ( 'description'       , description             );
                     formData.append ( 'categoriesIdsStr'  , categoriesIdsStr        );
                     formData.append ( 'keywordsIdsStr'    , keywordsIdsStr          );
                     formData.append ( 'publicPortal'      , publicPortal.toString() );
  if (adminId)      { formData.append ( 'adminId'           , adminId.toString()      ); }
  // Pas de fichier ici : si un nouveau fichier est fourni, il est uploadé séparément par chunks.

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();

  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'multipart/form-data',
	'User-Agent-Informations': `${userAgentInformations}`,
	'App-Informations': `${appInformations}`,
  };

  try
   {
    const response = await axios.post(url, formData, { headers });

    if (response.status === 200 || response.status === 201 || response.status === 204)
	      {
           if ( soundFileToUpload !== null ) { uploadSoundInBackground ( id , soundFileToUpload ); }

           return {
                    code    : 1 ,
                    comment : 'Sound updated successfully.',
                    payload : {} as any
                  };
          }
	 else {
           return handleError<void>(response.status, 'Non-200/204 response from API');
          }
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
	 {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<void>(-102, 'Unknown error');
  }
};

// Fonction pour supprimer un son
export const deleteSound = async (id: number,
                                            adminId?             : number   ): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.deleteSound}`;
  const url = baseUrl.replace('{id}', `${id}`);

  console.log ('deleteSound', url, id, adminId )

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();
  
  const headers = {
    'Accept': 'application/ld+json',
    'User-Agent-Informations': `${userAgentInformations}`,
    'App-Informations': `${appInformations}`,
    'X-Admin-Id': adminId ?? ''                    
  };


  try 
   {
    const response = await axios.delete(url, { headers });

  console.log ('deleteSound', response )

    if (response.status === 204) 
	      {
           return {
                    code    : 1,
                    comment : 'The sound has been successfully deleted.',
                    payload : {} as any
                  };
          } 
	 else {
           return handleError<void>(response.status, 'Error deleting the sound');
          }
   } 
  catch (error)
   {
  console.log ('deleteSound', error )

    if (axios.isAxiosError(error))
	 {
      if (error.response?.status === 409) {
        return {
          code    : -103,
          // Cf. api_audioSlideshow : la phrase du serveur nomme le diaporama qui retient ce son.
          comment : extractServerConflictMessage(error.response?.data),
          payload : {} as any
        };
      }
      if (error.response?.status === 404) {
        return {
          code    : -104,
          comment : 'The sound no longer exists.',
          payload : {} as any
        };
      }
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<void>(-102, 'Unknown error');
  }
};

export const createSound = async ( active            : boolean     ,
                                   title             : string      ,
                                   description       : string      ,
                                   soundFileToUpload : File | null ,
                                   categoriesIds     : number[]    ,
                                   keywordsIds       : number[]    ,
                                   publicPortal      : boolean     ,
                                   adminId?          : number      ): Promise<BackendResponse> => {

  const categoriesIdsStr: string = categoriesIds.join(',');
  const keywordsIdsStr: string = keywordsIds.join(',');

  const url = `${API_URLS.createSound}`;

  const formData = new FormData();

                     formData.append ( 'active'            , active.toString()       );
                     formData.append ( 'title'             , title                   );
                     formData.append ( 'description'       , description             );
                     formData.append ( 'categoriesIdsStr'  , categoriesIdsStr        );
                     formData.append ( 'keywordsIdsStr'    , keywordsIdsStr          );
                     formData.append ( 'publicPortal'      , publicPortal.toString() );
  if (adminId)      { formData.append ( 'adminId'           , adminId.toString()      ); }
  // Pas de fichier ici : la fiche est créée d'abord (métadonnées seules), puis le fichier
  // est uploadé séparément par chunks (uniforme quelle que soit sa taille, comme pour la vidéo).

  const userAgentInformations = getUserAgentInformations ();
  const appInformations       = getAppInformations ();

  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'multipart/form-data',
    'User-Agent-Informations': `${userAgentInformations}`,
    'App-Informations': `${appInformations}`,
  };

  try
   {
    const response = await axios.post(url, formData, { headers });

    if (response.status === 201)
	      {
           const soundId = response.data.id;

           if ( soundFileToUpload !== null ) { uploadSoundInBackground ( soundId , soundFileToUpload ); }

           return {
                    code    : 1,
                    comment : soundFileToUpload !== null ? 'Sound created successfully. Upload started in background.' : 'Sound created successfully.',
                    payload : soundId
                  };
          }
	 else {
           return handleError<void>(response.status, 'Error creating the sound');
          }
   }
  catch (error)
   {
    if (axios.isAxiosError(error))
	 {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<void>(-102, 'Unknown error');
  }
};

// AJOUT : Interface pour suivre la progression
export interface DownloadProgress {
  loaded: number;
  total: number;
}

/**
 * Télécharge un fichier audio avec suivi de progression fluide
 * et renvoie un objet Sound enrichi avec le blob et l’objectURL.
 */
export const downloadSoundBlob = async (
  sound: Sound,
  onProgress?: (progress: DownloadProgress) => void
): Promise<Sound> => {
  const baseUrl = `${API_URLS.getSoundFile}`;
  const url = baseUrl.replace("{id}", `${sound.id}`);

  console.log("🔊 Téléchargement du son :", url);

  if (!sound.soundFile) {
    console.warn("⚠️ Aucun soundFile défini pour ce son.");
    return sound;
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "blob";

    // Gestion de la progression
    xhr.onprogress = (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
        });
      }
    };

    // Succès
    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response as Blob;
        const objectURL = URL.createObjectURL(blob);
        console.log("✅ Téléchargement terminé :", sound.soundFile);
        resolve({
          ...sound,
          fileBlob: blob,
          objectURL,
        });
      } else {
        console.error("❌ Erreur HTTP lors du téléchargement :", xhr.status);
        reject(new Error(`Erreur HTTP ${xhr.status}`));
      }
    };

    // Erreur réseau
    xhr.onerror = () => {
      console.error("❌ Erreur réseau lors du téléchargement du son");
      reject(new Error("Erreur réseau lors du téléchargement du son"));
    };

    xhr.send();
  });
};
/**
 * Libère la mémoire utilisée par un Blob téléchargé
 * en révoquant son URL locale.
 */
export const releaseSoundBlob = (sound: Sound): Sound => {
  if (sound.objectURL) {
    URL.revokeObjectURL(sound.objectURL);
  }

  return {
    ...sound,
    fileBlob: undefined,
    objectURL: undefined,
  };
};

export const getSoundFileUrl = (soundId: number): string => {
  const baseUrl = `${API_URLS.getSoundFile}`;
  return baseUrl.replace('{id}', `${soundId}`);
};



export interface BackendResponse_SoundUsage extends BackendResponse<number[]> {}

export const getSoundUsage = async (soundId : number): Promise<BackendResponse_SoundUsage> => {

  const baseUrl = `${API_URLS.getSoundUsage}`;
  const url = baseUrl.replace('{id}', `${soundId}`);

  try 
   {
    const response = await axios.get(url);

    if (response.status === 200) 
          {
           return {
                    code    : 1,
                    comment : 'The retrieval of sound usage is complete.',
                    payload : response.data,
                  };
          } 
     else {
           return handleError<number[]>(response.status, 'Non-200 response from API');
          }
   } 
  catch (error) 
   {
    if (axios.isAxiosError(error)) 
     {
      return handleError<number[]>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<number[]>(-102, 'Unknown error');
  }
};
